import type {
  CreateCustomerRequest,
  Customer,
  CustomerFilters,
  UpdateCustomerRequest,
} from '@billx/types';

import { customerApiClient, userApiClient } from '../clients';
import type { NucleusServiceResponse } from './types';
import { handleAxiosError } from './utils';

export class CustomerService {
  static async createCustomer(
    customerData: CreateCustomerRequest
  ): Promise<NucleusServiceResponse<Customer>> {
    try {
      const response = await userApiClient.post('/customers', customerData);

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to create customer');
    }
  }

  static async fetchCustomers(
    filters: CustomerFilters
  ): Promise<NucleusServiceResponse<Customer[]>> {
    try {
      const params = new URLSearchParams();

      if (filters.businessId) {
        params.append('businessId', filters.businessId);
      }

      if (filters.phone) {
        params.append('phone', filters.phone);
      }

      if (filters.query) {
        params.append('query', filters.query);
      }

      if (filters.startDate) {
        params.append('startDate', filters.startDate.toISOString());
      }

      if (filters.endDate) {
        params.append('endDate', filters.endDate.toISOString());
      }

      if (filters.limit) {
        params.append('limit', filters.limit.toString());
      }

      if (filters.offset) {
        params.append('offset', filters.offset.toString());
      }

      const response = await userApiClient.get('/customers', { params });

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to fetch customers');
    }
  }

  static async updateCustomer(
    customerId: string,
    updateData: UpdateCustomerRequest,
    businessId?: string
  ): Promise<NucleusServiceResponse<Customer>> {
    try {
      const config = businessId ? { params: { businessId } } : {};
      const response = await userApiClient.put(
        `/customers/${customerId}`,
        updateData,
        config
      );

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to update customer');
    }
  }

  static async updateCustomerDue(
    customerId: string,
    dueAmount: number
  ): Promise<NucleusServiceResponse<Customer>> {
    try {
      const response = await userApiClient.put(`/customers/${customerId}/due`, {
        dueAmount,
      });

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to update customer dues');
    }
  }

  static async deleteCustomer(
    customerId: string
  ): Promise<NucleusServiceResponse<void>> {
    try {
      const response = await userApiClient.delete(`/customers/${customerId}`);

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to delete customer');
    }
  }

  static async findCustomerByPhone(
    phone: string,
    businessId: string
  ): Promise<NucleusServiceResponse<Customer>> {
    try {
      const params = new URLSearchParams();
      params.append('businessId', businessId);
      const response = await userApiClient.get(`/customers/phone/${phone}`, {
        params,
      });

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to find customer by phone');
    }
  }

  static async findCustomerById(
    customerId: string,
    businessId?: string
  ): Promise<NucleusServiceResponse<Customer>> {
    try {
      const config = businessId ? { params: { businessId } } : {};
      const response = await userApiClient.get(
        `/customers/${customerId}`,
        config
      );

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to find customer by ID');
    }
  }

  static async getCurrentCustomer(
    businessId: string
  ): Promise<NucleusServiceResponse<Customer>> {
    try {
      const response = await customerApiClient.get('/customers/me', {
        params: {
          businessId,
        },
      });

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to get current customer');
    }
  }
}
